<script setup lang="ts">
import type { Category } from '~/types/blog'
import { resolveImageAssetUrl } from '~/composables/useAssetUrl'
import { buildCategoryDirectoryMap, buildCategoryPath, buildPagePath, buildPostPath } from '~/composables/usePermalink'
import { normalizeFieldBool, normalizeFieldJson, normalizeFieldText } from '~/composables/useCustomFields'

const NATIVE_NAV_ITEMS = [
  { key: 'home', name: '首页', icon: 'fa-regular fa-compass' },
  { key: 'category', name: '分类', icon: 'fa-regular fa-folder' },
  { key: 'archives', name: '归档', icon: 'fa-solid fa-box-archive' },
  { key: 'tweet', name: '说说', icon: 'fa-regular fa-comment-dots' },
  { key: 'albums', name: '相册', icon: 'fa-regular fa-images' },
  { key: 'links', name: '友链', icon: 'fa-solid fa-link' }
] as const

type NativeNavKey = (typeof NATIVE_NAV_ITEMS)[number]['key']
type NativeNavItem = {
  name: string
  icon: string
}

const props = defineProps<{
  siteName: string
  siteSubtitle: string
  siteLogo?: string
  showSiteNameInNav?: boolean
  customFields?: Record<string, unknown>
  navItems?: Array<{ name?: string; path?: string; newTab?: boolean }>
  pageLinks?: Array<{ ID: number; slug: string; title?: string; sort?: number | string }>
  categories?: Category[]
  navContentLimit?: number
}>()

const route = useRoute()
const router = useRouter()
const blogApi = useBlogApi()
const customFields = computed(() => props.customFields)
const isSideNav = computed(() => normalizeFieldText(customFields.value, 'navMode', 'top') === 'side')
const navItemIcons = computed(() => {
  const value = normalizeFieldJson<unknown>(customFields.value, 'navItemIcons', [])
  return Array.isArray(value) ? value : []
})
// navIconAt 优先使用配置图标，侧边模式空值显示默认图标
const navIconAt = (index: number, defaultIcon: string) =>
  String(navItemIcons.value[index] ?? '').trim() || (isSideNav.value ? defaultIcon : '')
const nativeNavItems = computed<Record<NativeNavKey, NativeNavItem>>(() => {
  const rawLabels = normalizeFieldJson<unknown>(customFields.value, 'nativeNavLabels', [])
  const labels = Array.isArray(rawLabels) ? rawLabels : []

  return NATIVE_NAV_ITEMS.reduce((items, item, index) => {
    items[item.key] = {
      name: String(labels[index] ?? '').trim() || item.name,
      icon: navIconAt(index, item.icon)
    }
    return items
  }, {} as Record<NativeNavKey, NativeNavItem>)
})
const keyword = ref('')
const { keywords: searchHistory, remember: rememberSearch, clear: clearSearchHistory } = useSearchHistory()
const searchOpen = ref(false)
const searchAreaRef = ref<HTMLElement | null>(null)
const searchInputRef = ref<HTMLInputElement | null>(null)
const authMenuOpen = ref(false)
const authPopupTop = ref(0)
const sideMenuTop = ref(0)
const drawerOpen = ref(false)
const drawerCategoryOpen = ref(false)
const desktopDropdownOpen = ref<null | 'category' | 'more'>(null)
const colorMode = useColorMode()
const headerRef = ref<HTMLElement | null>(null)
const themeReady = ref(false)
const randomContentFetching = ref(false)
const routeNavigationLoading = useRouteNavigationLoading()
const randomContentLoading = computed(() => randomContentFetching.value || routeNavigationLoading.value)
const {
  user: authUser,
  userName: authUserName,
  isAuthenticated,
  loginLoading,
  logoutLoading,
  errorMessage: authErrorMessage,
  startLogin,
  bootstrapFromURL,
  logout
} = usePluginAuth()
const randomContentNotice = reactive({
  show: false,
  type: 'error' as 'error' | 'success',
  text: ''
})
const suggestionLoading = ref(false)
const suggestions = ref<Array<{ ID: number; slug: string; title: string; summary?: string; categorySlug?: string }>>([])
const shouldShowSuggestions = computed(() => searchOpen.value && keyword.value.trim().length > 0)
const showSuggestionEmpty = computed(() => shouldShowSuggestions.value && !suggestionLoading.value && suggestions.value.length === 0)
const highlightHtml = (value: string) => {
  const escaped = String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  return escaped
    .replace(/&lt;mark&gt;/gi, '<mark>')
    .replace(/&lt;\/mark&gt;/gi, '</mark>')
}

let suggestionTimer: ReturnType<typeof setTimeout> | null = null
let randomContentNoticeTimer: ReturnType<typeof setTimeout> | null = null
let suggestionQuerySeq = 0
let suggestionAbortController: AbortController | null = null
let removeOutsideClick: (() => void) | null = null

watch(
  () => route.query.keyword,
  value => {
    keyword.value = String(value || '')
  },
  { immediate: true }
)

watch(
  () => route.fullPath,
  () => {
    drawerOpen.value = false
    searchOpen.value = false
    authMenuOpen.value = false
    drawerCategoryOpen.value = false
    desktopDropdownOpen.value = null
  }
)

watch(
  drawerOpen,
  value => {
    if (!import.meta.client) {
      return
    }
    document.documentElement.style.overflow = value ? 'hidden' : ''
  }
)

const systemLinks = computed(() => {
  const fields = customFields.value
  return [
    { ...nativeNavItems.value.archives, path: '/archives', visible: normalizeFieldBool(fields, 'showArchivesNav', true) },
    { ...nativeNavItems.value.tweet, path: '/tweet', visible: normalizeFieldBool(fields, 'showTweetNav', true) },
    { ...nativeNavItems.value.albums, path: '/albums', visible: normalizeFieldBool(fields, 'showAlbumsNav', true) },
    { ...nativeNavItems.value.links, path: '/links', visible: normalizeFieldBool(fields, 'showLinksNav', true) }
  ].filter(item => item.visible)
})
type HeaderNavItem = {
  name: string
  path: string
  icon?: string
  newTab?: boolean
  external?: boolean
}
const isExternalLink = (path: string) => /^(https?:)?\/\//.test(path) || path.startsWith('mailto:') || path.startsWith('tel:')
const isPathActive = (path: string) => !isExternalLink(path) && route.path === path
const navContentLimit = computed(() => Math.max(1, Number(props.navContentLimit || 10) || 10))
const normalizedPageLinks = computed(() =>
  [...(props.pageLinks || [])]
    .filter(item => String(item?.slug || '').trim())
    .sort((a, b) => {
      const sortA = Number(a?.sort ?? 0)
      const sortB = Number(b?.sort ?? 0)
      if (sortA !== sortB) {
        return sortA - sortB
      }
      return (a?.ID || 0) - (b?.ID || 0)
    })
)
const navContentLinks = computed<HeaderNavItem[]>(() => {
  const links: HeaderNavItem[] = []
  for (const item of systemLinks.value) {
    links.push({ name: item.name, path: item.path, icon: item.icon, external: false })
  }
  for (const [index, item] of normalizedPageLinks.value.entries()) {
    links.push({
      name: item.title || item.slug,
      path: buildPagePath(customFields.value, item),
      icon: navIconAt(NATIVE_NAV_ITEMS.length + index, 'fa-regular fa-file-lines'),
      external: false
    })
  }
  const customNavIconOffset = NATIVE_NAV_ITEMS.length + normalizedPageLinks.value.length
  for (const [index, item] of (props.navItems || []).entries()) {
    const name = String(item?.name || '').trim()
    const path = String(item?.path || '').trim()
    if (!name || !path) {
      continue
    }
    links.push({
      name,
      path,
      icon: navIconAt(customNavIconOffset + index, 'fa-solid fa-link'),
      newTab: !!item.newTab,
      external: isExternalLink(path)
    })
  }
  return links
})
const flattenCategories = (nodes: Category[]): Category[] => {
  const sorted = [...nodes].sort((a, b) => (a.sort || 0) - (b.sort || 0))
  const result: Category[] = []
  for (const node of sorted) {
    if (!node?.slug || !node?.name) {
      continue
    }
    result.push(node)
    if ((node.children || []).length) {
      result.push(...flattenCategories(node.children || []))
    }
  }
  return result
}
const allCategories = computed(() => flattenCategories(props.categories || []))
// visibleNavContentCount 扣除首页与可见分类入口后计算剩余直显数量
const visibleNavContentCount = computed(() => Math.max(0, navContentLimit.value - 1 - Number(allCategories.value.length > 0)))
const visibleNavContentLinks = computed(() => navContentLinks.value.slice(0, visibleNavContentCount.value))
const overflowNavContentLinks = computed(() => navContentLinks.value.slice(visibleNavContentCount.value))
const isMoreActive = computed(() => overflowNavContentLinks.value.some(item => isPathActive(item.path)))
const categoryDirectoryMap = computed(() => buildCategoryDirectoryMap(props.categories || []))
const categoryPath = (item: Category) =>
  buildCategoryPath(customFields.value, item, { directory: categoryDirectoryMap.value.get(item.ID) || item.slug, categoryTree: props.categories || [] })
const isCategoryActive = computed(() => String(route.name || '').includes('category') || route.path.startsWith('/category/'))
let mediaQuery: MediaQueryList | null = null
const handleMediaChange = () => {
  if (mediaQuery?.matches) {
    drawerOpen.value = false
    return
  }
  searchOpen.value = false
}

const setDesktopDropdown = (key: null | 'category' | 'more') => {
  if (!isSideNav.value) {
    desktopDropdownOpen.value = key
  }
}

const closeDesktopDropdown = () => {
  desktopDropdownOpen.value = null
}

// handleDesktopDropdownMouseleave 保持侧边导航分组展开直到点击或离开焦点
const handleDesktopDropdownMouseleave = () => {
  if (!isSideNav.value) {
    closeDesktopDropdown()
  }
}

// toggleDesktopDropdown 切换导航菜单并按实际高度避开视口边缘
const toggleDesktopDropdown = async (key: 'category' | 'more', event: MouseEvent) => {
  const button = event.currentTarget as HTMLElement
  desktopDropdownOpen.value = desktopDropdownOpen.value === key ? null : key
  if (!isSideNav.value || desktopDropdownOpen.value === null) {
    return
  }
  await nextTick()
  if (desktopDropdownOpen.value !== key) {
    return
  }
  const menu = button.nextElementSibling as HTMLElement
  const menuHeight = Math.min(menu.scrollHeight, window.innerHeight - 32)
  sideMenuTop.value = Math.max(16, Math.min(button.getBoundingClientRect().top, window.innerHeight - menuHeight - 16))
}

const handleDesktopDropdownFocusout = (event: FocusEvent, key: 'category' | 'more') => {
  const currentTarget = event.currentTarget as HTMLElement | null
  const nextTarget = event.relatedTarget as Node | null
  if (currentTarget && nextTarget && currentTarget.contains(nextTarget)) {
    return
  }
  if (desktopDropdownOpen.value === key) {
    desktopDropdownOpen.value = null
  }
}

// openSearch 打开搜索面板并聚焦输入框
const openSearch = async () => {
  searchOpen.value = true
  await nextTick()
  searchInputRef.value?.focus()
}

const submitSearch = async () => {
  const value = keyword.value.trim()
  rememberSearch(value)
  searchOpen.value = false
  drawerOpen.value = false
  await router.push({ path: '/', query: value ? { keyword: value } : {} })
}

const submitDrawerSearch = async () => {
  const value = keyword.value.trim()
  rememberSearch(value)
  drawerOpen.value = false
  await router.push({ path: '/', query: value ? { keyword: value } : {} })
  keyword.value = ''
}

// selectSearchHistory 使用历史关键词重新搜索
const selectSearchHistory = async (value: string) => {
  keyword.value = value
  await submitSearch()
}

const gotoSuggestion = async (item: { ID: number; slug: string; categorySlug?: string }) => {
  if (!item?.slug) {
    return
  }
  rememberSearch(keyword.value)
  searchOpen.value = false
  await router.push(buildPostPath(customFields.value, { ID: item.ID, slug: item.slug }, { category: item.categorySlug || '' }))
}

const updateSuggestions = () => {
  if (!import.meta.client) {
    return
  }
  if (suggestionTimer) {
    clearTimeout(suggestionTimer)
    suggestionTimer = null
  }
  suggestionAbortController?.abort()
  suggestionAbortController = null
  const seq = ++suggestionQuerySeq
  const value = keyword.value.trim()
  if (!searchOpen.value || !value) {
    suggestionLoading.value = false
    suggestions.value = []
    return
  }
  suggestionLoading.value = true
  suggestions.value = []
  suggestionTimer = setTimeout(async () => {
    const controller = new AbortController()
    suggestionAbortController = controller
    try {
      const res = await blogApi.getContentMetaList({
        page: 1,
        pageSize: 8,
        type: 'post',
        keyword: value,
        sortBy: 'relevance'
      }, {
        signal: controller.signal,
        throwError: false
      })
      if (seq !== suggestionQuerySeq || controller.signal.aborted) {
        return
      }
      suggestions.value = (res?.list || [])
        .filter(item => item?.slug && item?.title)
        .map(item => ({
          ID: item.ID,
          slug: item.slug,
          title: item.title,
          summary: item.snippet || item.summary || '',
          categorySlug: item.categories?.[0]?.slug || ''
        }))
    } catch {
      if (seq !== suggestionQuerySeq) {
        return
      }
      suggestions.value = []
    } finally {
      if (suggestionAbortController === controller) {
        suggestionAbortController = null
      }
      if (seq === suggestionQuerySeq) {
        suggestionLoading.value = false
      }
    }
  }, 220)
}

const logoSrc = computed(() => resolveImageAssetUrl(props.siteLogo || ''))
const showSiteName = computed(() => props.showSiteNameInNav !== false)
const hasSiteSubtitle = computed(() => !!String(props.siteSubtitle || '').trim())
const showRandomContentButton = computed(() => normalizeFieldBool(customFields.value, 'showRandomContentButton', true))
const showLoginButton = computed(() => normalizeFieldBool(customFields.value, 'showLoginButton', true))
const uiThemeMode = computed(() => {
  if (!themeReady.value) {
    return 'system'
  }
  return colorMode.preference || 'system'
})
const toggleTheme = () => {
  const modes = ['system', 'light', 'dark']
  const currentIndex = modes.indexOf(uiThemeMode.value)
  const nextIndex = (currentIndex + 1) % modes.length

  colorMode.preference = modes[nextIndex]
}

// handleAuthClick 打开登录入口或当前用户菜单
const handleAuthClick = (event: MouseEvent) => {
  const button = event.currentTarget as HTMLElement
  authPopupTop.value = Math.max(16, Math.min(button.getBoundingClientRect().top, window.innerHeight - 200))
  if (!isAuthenticated.value) {
    void startLogin()
    return
  }
  authMenuOpen.value = !authMenuOpen.value
}

// handleLogout 退出 Engine 登录并关闭用户菜单
const handleLogout = async () => {
  await logout()
  authMenuOpen.value = false
}

const pushRandomContentNotice = (type: 'error' | 'success', text: string) => {
  randomContentNotice.type = type
  randomContentNotice.text = text
  randomContentNotice.show = true
  if (randomContentNoticeTimer) {
    clearTimeout(randomContentNoticeTimer)
  }
  randomContentNoticeTimer = setTimeout(() => {
    randomContentNotice.show = false
  }, 4000)
}

const gotoRandomContent = async () => {
  if (randomContentLoading.value) {
    return
  }
  randomContentFetching.value = true
  try {
    const item = await blogApi.getRandomContent()
    if (!item) {
      pushRandomContentNotice('error', '暂无可访问的文章')
      return
    }
    if (item.type === 'tweet') {
      await router.push('/tweet')
      return
    }
    if (!item.slug) {
      pushRandomContentNotice('error', '暂无可访问的文章')
      return
    }
    if (item.type === 'page') {
      await router.push(buildPagePath(customFields.value, { ID: item.id, slug: item.slug }))
      return
    }
    await router.push(buildPostPath(customFields.value, { ID: item.id, slug: item.slug }, { category: '' }))
  } catch (error) {
    pushRandomContentNotice('error', (error as Error).message || '随机文章获取失败')
  } finally {
    randomContentFetching.value = false
  }
}

const themeTitle = computed(() => {
  if (uiThemeMode.value === 'dark') {
    return '当前：深色'
  }
  if (uiThemeMode.value === 'light') {
    return '当前：浅色'
  }
  return '当前：自动'
})

const themeIconClass = computed(() => {
  if (uiThemeMode.value === 'dark') {
    return 'fa-solid fa-moon'
  }
  if (uiThemeMode.value === 'light') {
    return 'fa-solid fa-sun'
  }
  return 'fa-solid fa-circle-half-stroke'
})

const navActionProps = computed(() => ({
  themeTitle: themeTitle.value,
  themeIconClass: themeIconClass.value,
  showRandomContentButton: showRandomContentButton.value,
  randomContentLoading: randomContentLoading.value,
  showLoginButton: showLoginButton.value,
  isAuthenticated: isAuthenticated.value,
  authUserName: authUserName.value,
  authUserUUID: authUser.value?.uuid,
  loginLoading: loginLoading.value,
  logoutLoading: logoutLoading.value,
  authMenuOpen: authMenuOpen.value,
  authErrorMessage: authErrorMessage.value,
  authPopupTop: authPopupTop.value
}))

let headerResizeObserver: ResizeObserver | null = null
const syncAnchorOffset = () => {
  if (!import.meta.client) {
    return
  }
  const headerHeight = Math.max(0, Math.round(headerRef.value?.offsetHeight || 72))
  const anchorOffset = headerHeight + 16
  document.documentElement.style.setProperty('--anchor-offset', `${anchorOffset}px`)
}

onMounted(() => {
  themeReady.value = true
  if (!import.meta.client) {
    return
  }
  void bootstrapFromURL()
  mediaQuery = window.matchMedia('(min-width: 921px)')
  handleMediaChange()
  mediaQuery.addEventListener('change', handleMediaChange)
  syncAnchorOffset()
  window.addEventListener('resize', syncAnchorOffset, { passive: true })
  if (typeof ResizeObserver !== 'undefined' && headerRef.value) {
    headerResizeObserver = new ResizeObserver(() => {
      syncAnchorOffset()
    })
    headerResizeObserver.observe(headerRef.value)
  }
  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node | null
    if (!target) {
      return
    }
    if (searchOpen.value && !searchAreaRef.value?.contains(target)) {
      searchOpen.value = false
    }
    if (desktopDropdownOpen.value && !headerRef.value?.querySelector('.desktop-dropdown.open')?.contains(target)) {
      closeDesktopDropdown()
    }
    if (authMenuOpen.value && ![...(headerRef.value?.querySelectorAll('.auth-area') || [])].some(area => area.contains(target))) {
      authMenuOpen.value = false
    }
  }
  document.addEventListener('mousedown', handleClickOutside, true)
  removeOutsideClick = () => {
    document.removeEventListener('mousedown', handleClickOutside, true)
  }
})

onBeforeUnmount(() => {
  mediaQuery?.removeEventListener('change', handleMediaChange)
  window.removeEventListener('resize', syncAnchorOffset)
  headerResizeObserver?.disconnect()
  headerResizeObserver = null
  if (suggestionTimer) {
    clearTimeout(suggestionTimer)
    suggestionTimer = null
  }
  suggestionAbortController?.abort()
  suggestionAbortController = null
  if (randomContentNoticeTimer) {
    clearTimeout(randomContentNoticeTimer)
    randomContentNoticeTimer = null
  }
  removeOutsideClick?.()
  removeOutsideClick = null
  document.documentElement.style.overflow = ''
})

watch(
  () => [keyword.value, searchOpen.value],
  () => {
    updateSuggestions()
  }
)
</script>

<template>
  <header ref="headerRef" class="header-wrap" :class="{ 'header-side': isSideNav }">
    <div class="container header-inner">
      <div class="navigation-area">
        <NuxtLink to="/" :class="['brand-link', { 'logo-only': !showSiteName, 'no-logo': !logoSrc }]" aria-label="Home">
          <div v-if="logoSrc" class="brand-mark">
            <BlogImage :src="logoSrc" :alt="siteName" loading="eager" fetchpriority="high" :progressive="false" />
          </div>
          <div v-if="showSiteName" class="brand-text">
            <strong>{{ siteName }}</strong>
            <p v-if="hasSiteSubtitle">{{ siteSubtitle }}</p>
          </div>
        </NuxtLink>

        <nav class="nav-links desktop-nav" aria-label="主导航" :style="{ '--side-menu-top': `${sideMenuTop}px` }">
          <div class="nav-main" @scroll="closeDesktopDropdown">
            <NuxtLink to="/" class="desktop-nav-link">
            <BlogNavItemLabel :name="nativeNavItems.home.name" :icon="nativeNavItems.home.icon" :side="isSideNav" />
            </NuxtLink>
            <div
              v-if="allCategories.length"
              class="desktop-dropdown"
              :class="{ open: desktopDropdownOpen === 'category' }"
              @mouseenter="setDesktopDropdown('category')"
              @mouseleave="handleDesktopDropdownMouseleave"
              @focusin="setDesktopDropdown('category')"
              @focusout="handleDesktopDropdownFocusout($event, 'category')"
            >
              <button
                type="button"
                class="desktop-dropdown-trigger desktop-nav-link"
                :class="{ active: isCategoryActive }"
                :aria-expanded="desktopDropdownOpen === 'category'"
                @click="toggleDesktopDropdown('category', $event)"
              >
                <BlogNavItemLabel :name="nativeNavItems.category.name" :icon="nativeNavItems.category.icon" :side="isSideNav" />
                <i class="fa-solid fa-chevron-down" aria-hidden="true" />
              </button>
              <div class="desktop-dropdown-menu">
                <NuxtLink v-for="item in allCategories" :key="`cat:${item.ID}`" :to="categoryPath(item)" @click="closeDesktopDropdown">
                  {{ item.name }}
                </NuxtLink>
              </div>
            </div>
            <template v-for="item in visibleNavContentLinks" :key="`nav:${item.path}:${item.name}`">
              <a
                v-if="item.external"
                class="desktop-nav-link"
                :href="item.path"
                :target="item.newTab ? '_blank' : '_self'"
                :rel="item.newTab ? 'noopener noreferrer' : undefined"
              >
                <BlogNavItemLabel :name="item.name" :icon="item.icon" :side="isSideNav" />
              </a>
              <NuxtLink v-else :to="item.path" class="desktop-nav-link">
                <BlogNavItemLabel :name="item.name" :icon="item.icon" :side="isSideNav" />
              </NuxtLink>
            </template>
            <div
              v-if="overflowNavContentLinks.length"
              class="desktop-dropdown"
              :class="{ open: desktopDropdownOpen === 'more' }"
              @mouseenter="setDesktopDropdown('more')"
              @mouseleave="handleDesktopDropdownMouseleave"
              @focusin="setDesktopDropdown('more')"
              @focusout="handleDesktopDropdownFocusout($event, 'more')"
            >
              <button
                type="button"
                class="desktop-dropdown-trigger desktop-nav-link"
                :class="{ active: isMoreActive }"
                :aria-expanded="desktopDropdownOpen === 'more'"
                @click="toggleDesktopDropdown('more', $event)"
              >
                <BlogNavItemLabel name="更多" :icon="isSideNav ? 'fa-solid fa-bars' : ''" :side="isSideNav" />
                <i class="fa-solid fa-chevron-down" aria-hidden="true" />
              </button>
              <div class="desktop-dropdown-menu">
                <template v-for="item in overflowNavContentLinks" :key="`more:${item.path}:${item.name}`">
                  <a
                    v-if="item.external"
                    :href="item.path"
                    :target="item.newTab ? '_blank' : '_self'"
                    :rel="item.newTab ? 'noopener noreferrer' : undefined"
                    @click="closeDesktopDropdown"
                  >
                    <BlogNavItemLabel :name="item.name" :icon="item.icon" />
                  </a>
                  <NuxtLink v-else :to="item.path" @click="closeDesktopDropdown">
                    <BlogNavItemLabel :name="item.name" :icon="item.icon" />
                  </NuxtLink>
                </template>
              </div>
            </div>
            <BlogNavActions
              v-if="isSideNav && showLoginButton"
              side
              section="account"
              v-bind="navActionProps"
              @auth="handleAuthClick"
              @logout="handleLogout"
            />
          </div>
          <div class="nav-bottom">
            <BlogNavActions
              v-if="isSideNav"
              side
              section="utilities"
              v-bind="navActionProps"
              @search="openSearch"
              @theme="toggleTheme"
              @random="gotoRandomContent"
            />
          </div>
        </nav>
      </div>

      <div class="actions">
        <div ref="searchAreaRef" class="search-area">
          <button class="search-icon-btn" type="button" aria-label="搜索" @click="openSearch">
            <i class="fa-solid fa-magnifying-glass" aria-hidden="true" />
          </button>
          <Transition name="search-pop-fade">
            <form v-if="searchOpen || isSideNav" class="search-pop" @submit.prevent="submitSearch">
              <div class="search-pop-row">
                <div class="search-pop-input-wrap">
                  <input ref="searchInputRef" v-model="keyword" class="search-pop-input" type="text" aria-label="搜索标题或正文关键字" placeholder="搜索标题或正文关键字" @click="openSearch" @focus="searchOpen = true" @keydown.esc="searchOpen = false" />
                  <button class="search-pop-submit" type="submit" aria-label="搜索">
                    <i class="fa-solid fa-magnifying-glass" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <div v-if="shouldShowSuggestions" class="search-suggest-list" :class="{ 'side-search-suggestions': isSideNav }">
                <div v-if="suggestionLoading" class="search-suggest-loading">
                  <BlogLoadingIndicator size="large" text="正在搜索..." />
                </div>
                <template v-else>
                  <button
                    v-for="item in suggestions"
                    :key="item.ID"
                    class="search-suggest-item"
                    type="button"
                    @click="gotoSuggestion(item)"
                  >
                    <span class="search-suggest-title" v-html="highlightHtml(item.title)" />
                    <span v-if="item.summary" class="search-suggest-summary" v-html="highlightHtml(item.summary)" />
                  </button>
                  <div v-if="showSuggestionEmpty" class="search-suggest-empty">
                    <i class="fa-regular fa-folder-open" aria-hidden="true" />
                    <span>暂无匹配结果</span>
                  </div>
                </template>
              </div>
              <div v-else-if="searchOpen" :class="{ 'side-search-suggestions': isSideNav }">
                <BlogSearchHistory
                  v-if="searchHistory.length"
                  :keywords="searchHistory"
                  @select="selectSearchHistory"
                  @clear="clearSearchHistory"
                />
                <div v-else class="search-suggest-empty">
                  <i class="fa-regular fa-folder-open" aria-hidden="true" />
                  <span>暂无内容</span>
                </div>
              </div>
            </form>
          </Transition>
        </div>
        <BlogNavActions
          class="toolbar-controls"
          v-bind="navActionProps"
          @theme="toggleTheme"
          @random="gotoRandomContent"
          @auth="handleAuthClick"
          @logout="handleLogout"
        />
        <button class="menu-btn" type="button" aria-label="菜单" @click="drawerOpen = true">
          <i class="fa-solid fa-bars" aria-hidden="true" />
        </button>
        <Transition name="fade-slide">
          <p
            v-if="randomContentNotice.show"
            class="random-content-notice"
            :class="`is-${randomContentNotice.type}`"
          >
            {{ randomContentNotice.text }}
          </p>
        </Transition>
      </div>
    </div>

    <Transition name="drawer-fade">
      <div v-if="drawerOpen" class="drawer-mask" @click="drawerOpen = false">
        <div class="drawer-backdrop" aria-hidden="true" />
        <div class="drawer" @click.stop>
          <div class="drawer-head">
            <h3 class="drawer-title"><i class="fa-regular fa-compass" aria-hidden="true" />导航</h3>
            <button type="button" class="drawer-close" aria-label="关闭菜单" @click="drawerOpen = false">
              <i class="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          </div>

          <div class="drawer-search-area">
            <form class="drawer-search" @submit.prevent="submitDrawerSearch">
              <div class="drawer-search-input-wrap">
                <input v-model="keyword" type="text" placeholder="搜索文章" />
              </div>
              <button class="drawer-search-submit" type="submit" aria-label="搜索">
                <i class="fa-solid fa-magnifying-glass" aria-hidden="true" />
              </button>
            </form>
            <BlogSearchHistory
              v-if="searchHistory.length && !keyword.trim()"
              :keywords="searchHistory"
              @select="selectSearchHistory"
              @clear="clearSearchHistory"
            />
          </div>

          <nav class="drawer-links">
            <NuxtLink to="/">
              <BlogNavItemLabel :name="nativeNavItems.home.name" :icon="nativeNavItems.home.icon" />
            </NuxtLink>
            <div v-if="allCategories.length" class="drawer-group">
              <button
                type="button"
                class="drawer-group-trigger"
                :class="{ active: isCategoryActive }"
                @click="drawerCategoryOpen = !drawerCategoryOpen"
              >
                <BlogNavItemLabel :name="nativeNavItems.category.name" :icon="nativeNavItems.category.icon" />
                <i :class="drawerCategoryOpen ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'" aria-hidden="true" />
              </button>
              <div class="drawer-sub-links" :class="{ open: drawerCategoryOpen }">
                <div class="drawer-sub-links-inner">
                  <NuxtLink v-for="item in allCategories" :key="`drawer:cat:${item.ID}`" :to="categoryPath(item)">
                    {{ item.name }}
                  </NuxtLink>
                </div>
              </div>
            </div>
            <template v-for="item in navContentLinks" :key="`drawer:${item.path}:${item.name}`">
              <a
                v-if="item.external"
                :href="item.path"
                :target="item.newTab ? '_blank' : '_self'"
                :rel="item.newTab ? 'noopener noreferrer' : undefined"
              >
                <BlogNavItemLabel :name="item.name" :icon="item.icon" />
              </a>
              <NuxtLink v-else :to="item.path">
                <BlogNavItemLabel :name="item.name" :icon="item.icon" />
              </NuxtLink>
            </template>
          </nav>
        </div>
      </div>
    </Transition>
  </header>
</template>

<style scoped>
.header-wrap {
  position: sticky;
  top: 0;
  z-index: 30;
}

/* 模糊效果放在背景层，避免影响侧边导航的 fixed 定位 */
.header-wrap::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
}

.navigation-area {
  display: contents;
}

.header-inner {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 72px;
}

.brand-link {
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
  color: inherit;
  text-decoration: none;
}

.brand-link.logo-only {
  gap: 0;
}

.brand-link.no-logo {
  gap: 0;
}

.brand-text strong {
  display: block;
  font-size: 18px;
}

.brand-text p {
  margin-top: 2px;
  font-size: 13px;
  color: var(--text-soft);
}

.brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  flex: none;
  background: transparent;
}

.brand-mark img {
  display: block;
  width: auto;
  height: 100%;
  object-fit: contain;
  background: transparent;
}

.desktop-nav {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  gap: 8px;
  overflow: visible;
  font-size: 16px;
  font-weight: 600;
}

.nav-main,
.nav-bottom {
  display: contents;
}

.desktop-nav-link {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 7px 10px;
  border-radius: var(--radius);
  color: var(--text);
  text-decoration: none;
  background: transparent;
  transition: color 0.18s ease;
}

.desktop-nav-link :deep(.nav-item-text) {
  position: relative;
  padding-bottom: 7px;
  margin-bottom: -7px;
}

.desktop-nav-link :deep(.nav-item-text)::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  border-radius: 999px;
  background: linear-gradient(90deg, color-mix(in srgb, var(--brand) 72%, #fff 28%), var(--brand));
  box-shadow: 0 0 10px color-mix(in srgb, var(--brand) 24%, transparent);
  transform: scaleX(0.32);
  transform-origin: center;
  opacity: 0;
  transition:
    opacity 0.18s ease,
    transform 0.22s ease;
}

.desktop-nav-link:hover {
  text-decoration: none;
  color: var(--brand);
}

.desktop-nav a.router-link-active,
.desktop-nav-link.active {
  color: var(--brand);
}

.desktop-nav .nav-main > a.router-link-active :deep(.nav-item-text)::after {
  opacity: 1;
  transform: scaleX(1);
}

.desktop-dropdown {
  position: relative;
  padding-bottom: 8px;
  margin-bottom: -8px;
}

.desktop-dropdown::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  height: 8px;
}

.desktop-dropdown-trigger {
  font: inherit;
  line-height: inherit;
  border: 0;
  cursor: pointer;
  appearance: none;
}

.desktop-dropdown-trigger:hover {
  text-decoration: none;
}

.desktop-dropdown-menu {
  position: absolute;
  left: 0;
  top: calc(100% + 8px);
  z-index: 5;
  min-width: 170px;
  display: none;
  padding: 8px;
  border: 0;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  background: var(--bg-soft);
  box-shadow: var(--floating-shadow);
}

.desktop-dropdown.open .desktop-dropdown-menu {
  display: grid;
  gap: 6px;
}

.desktop-dropdown-menu a {
  color: var(--text);
  text-decoration: none;
  white-space: nowrap;
  padding: 7px 10px;
  border-radius: 8px;
  transition:
    color 0.18s ease,
    background-color 0.18s ease;
}

.desktop-dropdown-menu a:hover {
  text-decoration: none;
  color: var(--brand);
  background: var(--brand-soft);
}

.desktop-dropdown-menu a.router-link-active {
  color: var(--brand);
  background: color-mix(in srgb, var(--brand-soft) 78%, var(--bg) 22%);
}

.actions {
  position: relative;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.search-icon-btn,
.menu-btn,
.drawer-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  border: 0;
  border-radius: 999px;
  color: var(--text);
  cursor: pointer;
  background: transparent;
}

.search-icon-btn,
.menu-btn,
.drawer-close {
  width: 34px;
  padding: 0;
}

.search-icon-btn i,
.menu-btn i,
.drawer-close i {
  font-size: 16px;
  line-height: 1;
}

.search-pop {
  border: 0;
  border-radius: var(--radius);
  background: var(--bg-soft);
  box-shadow: var(--floating-shadow);
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 12;
  display: grid;
  gap: 10px;
  width: min(420px, calc(100vw - 2rem));
  padding: 12px;
}

.search-area {
  position: relative;
}

.random-content-notice {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  margin: 0;
  padding: 7px 10px;
  border-radius: 9px;
  font-size: 12px;
  line-height: 1.5;
  white-space: nowrap;
  border: 1px solid var(--line);
  background: var(--bg);
  box-shadow: var(--shadow);
  z-index: 13;
}

.random-content-notice.is-error {
  color: #c03636;
  border-color: color-mix(in srgb, #c03636 24%, var(--line));
  background: color-mix(in srgb, #c03636 8%, var(--bg));
}

.search-pop-row {
  display: flex;
  align-items: center;
}

.search-pop-input-wrap {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 42px;
  display: flex;
  align-items: center;
  padding: 0 4px 0 12px;
  border-radius: 999px;
  background: var(--editor-bg);
  transition: background-color 0.18s ease;
}

.search-pop-input-wrap:focus-within {
  background: color-mix(in srgb, var(--brand) 4%, var(--editor-bg));
}

.search-pop-input {
  border: 0;
  width: 100%;
  min-width: 0;
  height: 34px;
  padding: 0;
  font-size: 14px;
  color: var(--text);
  background: transparent;
}

.search-pop-input:focus {
  outline: none;
}

.search-pop-submit {
  flex: none;
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 999px;
  padding: 0;
  color: var(--text-soft);
  cursor: pointer;
  background: transparent;
  transition: color 0.16s ease;
}

.search-pop-submit:hover {
  color: var(--brand);
}

.search-pop-submit i {
  font-size: 14px;
  line-height: 1;
}

.search-suggest-list {
  display: grid;
  gap: 6px;
  max-height: 310px;
  margin-top: 4px;
  overflow-y: auto;
}

.search-suggest-item {
  display: grid;
  gap: 2px;
  width: 100%;
  text-align: left;
  font: inherit;
  border: 0;
  border-radius: var(--radius-sm);
  padding: 9px 10px;
  color: var(--text);
  cursor: pointer;
  background: transparent;
  transition:
    color 0.18s ease,
    background-color 0.18s ease;
}

.search-suggest-item:hover {
  color: var(--brand);
  background: var(--brand-soft);
}

.search-suggest-title {
  font-size: 14px;
  line-height: 1.3;
  color: inherit;
}

.search-suggest-summary {
  font-size: 12px;
  line-height: 1.35;
  color: var(--text-soft);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-suggest-item :deep(mark) {
  padding: 0 2px;
  border-radius: 4px;
  background: color-mix(in srgb, var(--brand) 22%, transparent);
  color: inherit;
}

.search-suggest-loading,
.search-suggest-empty {
  min-height: 112px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-soft);
  font-size: 13px;
  padding: 8px 10px;
}

.search-suggest-loading {
  gap: 8px;
}

.search-suggest-empty {
  flex-direction: column;
  gap: 8px;
}

.search-suggest-empty i {
  font-size: 24px;
}

.search-pop-fade-enter-active,
.search-pop-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.search-pop-fade-enter-from,
.search-pop-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
}

.menu-btn {
  display: none;
}

.drawer-mask {
  position: fixed;
  inset: 0;
  z-index: 50;
  height: 100dvh;
  overflow: hidden;
}

.drawer-backdrop {
  position: fixed;
  inset: 0;
  height: 100dvh;
  background: rgba(10, 16, 22, 0.56);
  backdrop-filter: blur(2px);
}

.drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(68vw, 264px);
  height: 100dvh;
  max-height: 100dvh;
  padding: 16px;
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  gap: 14px;
  align-content: flex-start;
  background: var(--bg-soft);
  box-shadow: var(--floating-shadow);
  transform: translateX(0);
  will-change: transform;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

@supports not (height: 100dvh) {
  .drawer-mask,
  .drawer-backdrop,
  .drawer {
    height: 100vh;
    max-height: 100vh;
  }
}

.drawer-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.drawer-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-family: inherit;
}

.drawer-title i {
  font-size: 17px;
  color: var(--brand);
}

.drawer-close {
  color: var(--text-soft);
}

.drawer-search-area {
  display: grid;
  gap: 12px;
  min-width: 0;
}

.drawer-search {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: 42px;
  padding: 0;
  box-sizing: border-box;
}

.drawer-search-input-wrap {
  display: flex;
  align-items: center;
  gap: 0;
  min-height: 42px;
  padding: 0 12px;
  border-radius: 999px;
  background: var(--editor-bg);
  box-sizing: border-box;
  transition: background-color 0.16s ease;
}

.drawer-search-input-wrap:focus-within {
  background: color-mix(in srgb, var(--brand) 4%, var(--editor-bg));
}

.drawer-search input {
  border: 0;
  min-width: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  color: var(--text);
  font-size: 14px;
  background: transparent;
}

.drawer-search input::placeholder {
  color: var(--text-soft);
}

.drawer-search input:focus {
  outline: none;
}

.drawer-search-submit {
  flex: none;
  border: 0;
  width: 42px;
  height: 42px;
  border-radius: 999px;
  margin-left: 6px;
  padding: 0;
  white-space: nowrap;
  color: var(--brand);
  cursor: pointer;
  background: color-mix(in srgb, var(--brand-soft) 70%, var(--bg-soft) 30%);
  transition:
    color 0.16s ease,
    background-color 0.16s ease;
}

.drawer-search-submit:hover {
  color: #fff;
  background: var(--brand);
}

.drawer-search-submit:active {
  background: color-mix(in srgb, var(--brand) 86%, #000 14%);
}

.drawer-search-submit i {
  font-size: 13px;
  line-height: 1;
}

.drawer-links {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  font-weight: 600;
}

.drawer-links a,
.drawer-group-trigger {
  display: flex;
  align-items: center;
  border: 0;
  border-radius: 999px;
  padding: 9px 12px;
  min-height: 42px;
  line-height: 1.35;
  color: var(--text);
  text-decoration: none;
  background: transparent;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.drawer-group-trigger {
  width: 100%;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
  font: inherit;
  flex-shrink: 0;
}

.drawer-links a:hover,
.drawer-group-trigger:hover {
  text-decoration: none;
  color: var(--brand);
  background: var(--brand-soft);
}

.drawer-links a.router-link-active,
.drawer-group-trigger.active {
  color: var(--brand);
  background: var(--brand-soft);
}

.drawer-group {
  display: grid;
}

.drawer-sub-links {
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  transition:
    grid-template-rows 0.2s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.2s ease;
  will-change: grid-template-rows, opacity;
}

.drawer-sub-links.open {
  grid-template-rows: 1fr;
  opacity: 1;
}

.drawer-sub-links-inner {
  display: grid;
  gap: 8px;
  padding-top: 0;
  padding-left: 10px;
  overflow: hidden;
}

.drawer-sub-links.open .drawer-sub-links-inner {
  padding-top: 8px;
}

.drawer-fade-enter-active,
.drawer-fade-leave-active {
  transition: opacity 0.18s ease;
}

.drawer-fade-enter-from,
.drawer-fade-leave-to {
  opacity: 0;
}

.drawer-fade-enter-active .drawer,
.drawer-fade-leave-active .drawer {
  transition: transform 0.2s ease;
}

.drawer-fade-enter-from .drawer,
.drawer-fade-leave-to .drawer {
  transform: translateX(100%);
}

@media (max-width: 920px) {
  .header-wrap {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
  }

  .desktop-nav {
    display: none;
  }

  .header-inner {
    justify-content: space-between;
    gap: 8px;
  }

  .menu-btn {
    display: inline-flex;
  }

  .search-area {
    display: none;
  }

  .search-pop {
    left: 0;
    right: 0;
    width: auto;
  }

  .brand-link {
    min-width: 0;
  }

  .brand-text p {
    display: none;
  }

  .drawer-backdrop {
    backdrop-filter: none;
  }
}

@media (max-width: 560px) {
  .brand-text strong {
    font-size: 16px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 44vw;
  }
}

@media (min-width: 921px) {
  .header-side .header-inner {
    width: var(--site-layout-width);
    gap: var(--site-nav-gap);
  }

  .header-side .navigation-area {
    position: fixed;
    top: 0;
    bottom: 0;
    left: var(--site-nav-left);
    z-index: 1;
    display: flex;
    flex-direction: column;
    width: var(--site-nav-width);
    padding-bottom: 24px;
    background: var(--bg);
  }

  .header-side .navigation-area::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 100%;
    width: var(--site-nav-gap);
    background: linear-gradient(to right, var(--bg), transparent);
    pointer-events: none;
  }

  .header-side .brand-link {
    width: 100%;
    min-width: 0;
    height: calc(var(--anchor-offset) + 14px);
    padding: 24px 20px;
  }

  .header-side .brand-text {
    min-width: 0;
  }

  .header-side .brand-text strong,
  .header-side .brand-text p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .header-side .desktop-nav {
    min-height: 0;
    flex-direction: column;
    justify-content: space-between;
    gap: 24px;
  }

  .header-side .nav-main {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .header-side .nav-bottom {
    display: grid;
    flex-shrink: 0;
  }

  .header-side .desktop-nav-link {
    flex-shrink: 0;
    min-height: 52px;
    padding: 12px 20px;
    border-radius: 999px;
    transition: background-color 0.18s ease;
  }

  .header-side .desktop-nav-link:hover,
  .header-side .desktop-nav-link.router-link-active,
  .header-side .desktop-nav-link.active {
    background: color-mix(in srgb, var(--text) 7%, var(--bg));
    color: var(--text);
  }

  .header-side .desktop-nav-link :deep(.nav-item-text) {
    padding-bottom: 0;
    margin-bottom: 0;
  }

  .header-side .desktop-nav-link :deep(.nav-item-text)::after,
  .header-side .desktop-dropdown::after,
  .header-side .toolbar-controls {
    display: none;
  }

  .header-side .desktop-dropdown {
    flex-shrink: 0;
    padding-bottom: 0;
    margin-bottom: 0;
  }

  .header-side .desktop-dropdown-trigger {
    width: 100%;
    text-align: left;
  }

  .header-side .desktop-dropdown-trigger > .fa-chevron-down {
    margin-left: auto;
    font-size: 12px;
    transform: rotate(-90deg);
  }

  .header-side .desktop-dropdown-menu {
    position: fixed;
    top: var(--side-menu-top);
    left: calc(var(--site-nav-left) + var(--site-nav-width) + 12px);
    width: 180px;
    max-height: calc(100dvh - var(--side-menu-top) - 16px);
    overflow-y: auto;
  }

  .header-side .desktop-dropdown-menu a {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .header-side .actions {
    position: static;
    flex: 1;
    min-width: 0;
  }

  .header-side .search-area {
    position: absolute;
    top: 50%;
    left: 50%;
    width: min(440px, calc(100% - 2 * (var(--site-nav-width) + var(--site-nav-gap))));
    min-width: 0;
    margin: 0;
    transform: translate(-50%, -50%);
  }

  .header-side .search-icon-btn {
    display: none;
  }

  .header-side .search-pop {
    position: relative;
    top: auto;
    width: 100%;
    padding: 0;
    background: transparent;
    box-shadow: none;
  }

  .header-side .search-pop-row {
    padding: 0 4px 0 6px;
    border: 1px solid var(--line);
    border-radius: 999px;
    background: color-mix(in srgb, var(--bg-soft) 60%, transparent);
  }

  .header-side .search-pop-input-wrap {
    background: transparent;
  }

  .header-side .side-search-suggestions {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    margin: 0;
    padding: 12px;
    border-radius: var(--radius);
    background: var(--bg-soft);
    box-shadow: var(--floating-shadow);
  }

  .drawer-mask {
    display: none !important;
  }
}
</style>
